import React, { useEffect, useMemo, useState } from "react";
import { FaBan, FaQrcode, FaCoffee, FaIdCard, FaUserAlt } from "react-icons/fa";
import useAuth from "../../Hooks/useAuth";
import { useGetPaymentRejectionsQuery } from "../../store/storageRoles/devicesSharedApi";
import { EmptyState, ListView, ListToolbar, ListSkeleton } from "../shared";
import CustomDropdown from "../atoms/CustomDropdown";
import Pagination from "../atoms/Pagination";
import { formatKurus, rejectionLabel, REJECTION_CODE_LABELS } from "../../utils/enduserWallet";

const PAGE_SIZE = 25;

const CHANNEL = {
  qr: { icon: FaQrcode, label: "QR" },
  espresso: { icon: FaCoffee, label: "Espresso QR" },
  nfc: { icon: FaIdCard, label: "Kart (NFC)" },
};

// Kullanıcı tarafı (alıcı kuralı / bakiye) turuncu, makine tarafı gri
const MACHINE_CODES = ["DEVICE_OFFLINE", "DEVICE_BUSY", "DEVICE_REJECTED"];
const reasonChip = (code) =>
  MACHINE_CODES.includes(code)
    ? "bg-gray-100 text-gray-700 border-gray-200"
    : "bg-orange-50 text-orange-700 border-orange-200";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "";

const userName = (row) =>
  [row.user?.name, row.user?.lastName].filter(Boolean).join(" ") || row.user?.email || "Silinmiş kullanıcı";

const Cells = {
  dateTime: (row) => (
    <span className="inline-flex flex-col leading-tight">
      <span className="text-sm font-SemiBold text-gray-800">{fmtDate(row.createdAt)}</span>
      <span className="text-xs text-gray-500">{fmtTime(row.createdAt)}</span>
    </span>
  ),
  user: (row) => (
    <span className="inline-flex flex-col leading-tight min-w-0">
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-800">
        <FaUserAlt className="w-3 h-3 text-gray-400 shrink-0" />
        {userName(row)}
      </span>
      <span className="text-xs text-gray-500 truncate">{row.userFirm?.firmName || "Firmasız"}</span>
    </span>
  ),
  product: (row) => (
    <span className="inline-flex flex-col leading-tight">
      <span className="text-sm text-gray-800">{row.productName || "-"}</span>
      <span className="text-xs text-gray-500">{row.price > 0 ? formatKurus(row.price) : ""}</span>
    </span>
  ),
  reason: (row) => (
    <span className="inline-flex flex-col gap-1 min-w-0">
      <span
        className={`inline-flex w-fit items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-SemiBold whitespace-nowrap ${reasonChip(row.code)}`}
      >
        <FaBan className="w-3 h-3 shrink-0" />
        {rejectionLabel(row.code)}
      </span>
      <span className="text-xs text-gray-500 line-clamp-2" title={row.message}>
        {row.message}
      </span>
    </span>
  ),
  channel: (row) => {
    const ch = CHANNEL[row.channel] || { icon: FaQrcode, label: row.channel };
    const Icon = ch.icon;
    return (
      <span className="inline-flex flex-col leading-tight">
        <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
          <Icon className="w-3 h-3 text-gray-400 shrink-0" />
          {ch.label}
        </span>
        <span className="text-xs text-gray-500">{row.stage === "scan" ? "Taramada" : "Ödemede"}</span>
      </span>
    );
  },
};

const columns = [
  { key: "dateTime", label: "Tarih / Saat", cellClassName: "px-4 py-3 whitespace-nowrap", render: Cells.dateTime },
  { key: "user", label: "Kullanıcı", cellClassName: "px-4 py-3", render: Cells.user },
  { key: "product", label: "Ürün / Tutar", cellClassName: "px-4 py-3", render: Cells.product },
  { key: "reason", label: "Neden", cellClassName: "px-4 py-3 align-top", render: Cells.reason },
  { key: "channel", label: "Kanal", cellClassName: "px-4 py-3 whitespace-nowrap", render: Cells.channel },
];

/**
 * Cihazın reddedilen ödemeleri (#410) — QR / Espresso QR / NFC. Son 90 gün,
 * en çok 200 kayıt tek istekte gelir; neden filtresi ve sayfalama client'ta.
 * Sözleşme: BayinerIOT-Workspace- docs/api-contracts/employee-only-payment-device.md §10.1
 */
export default function PaymentRejectionsTable({ deviceId, token: tokenProp }) {
  const { auth } = useAuth();
  const token = tokenProp || auth?.accessToken;
  const { data, isLoading, isFetching, isError } = useGetPaymentRejectionsQuery(
    { id: deviceId, token, limit: 200 },
    { skip: !deviceId || !token }
  );
  const [codeOpt, setCodeOpt] = useState(null);
  const [page, setPage] = useState(1);

  const allRows = useMemo(() => data?.data?.rejections || [], [data]);
  const last7Days = data?.data?.last7Days;

  // Yalnız veride bulunan nedenler seçilebilir
  const codeOptions = useMemo(() => {
    const seen = [...new Set(allRows.map((r) => r.code))];
    return seen.map((code) => ({
      id: code,
      label: REJECTION_CODE_LABELS[code] || code,
      value: code,
    }));
  }, [allRows]);

  const rows = useMemo(
    () => (codeOpt?.value ? allRows.filter((r) => r.code === codeOpt.value) : allRows),
    [allRows, codeOpt]
  );

  useEffect(() => {
    setPage(1);
  }, [codeOpt]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [rows, page]);

  const renderMobileCard = (row) => (
    <div key={row._id} className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        {Cells.reason(row)}
        <span className="text-xs text-gray-500 shrink-0">
          {fmtDate(row.createdAt)} {fmtTime(row.createdAt)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
        <span className="truncate">{userName(row)}</span>
        <span className="shrink-0">
          {row.productName || "-"} {row.price > 0 && `· ${formatKurus(row.price)}`}
        </span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full">
      <ListToolbar
        icon={FaBan}
        title="Reddedilen Ödemeler"
        subtitle={Number.isFinite(last7Days) ? `Son 7 gün: ${last7Days} ret · kayıtlar 90 gün tutulur` : undefined}
        count={allRows.length}
        isLoading={isLoading}
        filters={
          <CustomDropdown
            label="Neden"
            value={codeOpt}
            onChange={(o) => setCodeOpt(o?.id === "all" ? null : o)}
            options={codeOptions}
            allOption={{ id: "all", label: "Tüm Nedenler" }}
            placeholder="Tüm Nedenler"
            compact
            className="w-full sm:w-60"
          />
        }
      />
      <ListView
        headers={columns.map((c) => ({ label: c.label }))}
        renderRow={(row) => (
          <tr className="bg-white hover:bg-gray-50 transition-colors">
            {columns.map((c) => (
              <td key={c.key} className={c.cellClassName}>
                {c.render(row)}
              </td>
            ))}
          </tr>
        )}
        data={pageRows}
        rowKey="_id"
        renderMobileCard={renderMobileCard}
        isLoading={isLoading || isFetching}
        loadingNode={<ListSkeleton columns={columns} message="Reddedilen ödemeler yükleniyor..." />}
        empty={
          isError ? (
            <EmptyState
              message="Reddedilen ödemeler yüklenemedi"
              suggestion="Bağlantıyı kontrol edip tekrar deneyin."
            />
          ) : (
            <EmptyState
              message={allRows.length ? "Bu nedenle ret yok" : "Reddedilen ödeme yok"}
              suggestion={
                allRows.length
                  ? "Neden filtresini değiştirmeyi deneyin."
                  : "Son 90 günde bu makinede reddedilen ödeme kaydı bulunmuyor."
              }
            />
          )
        }
        footer={
          totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              itemsPerPage={PAGE_SIZE}
              totalItems={rows.length}
            />
          )
        }
      />
    </div>
  );
}
